import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { AppBar, Box, Button, Container, Fade } from "@mui/material";
import { PrimaryBar } from "@platform/design-system";
import {
  CARRIERS_REP_DIRECTORY,
  CONSULTANT_VIEW,
} from "@platform/feature-flags";
import { submitEvent } from "@platform/browser-logger";
import { useIsScrolling } from "@platform/hooks";
import { ROUTE_NAMES, UserType } from "@platform/types";
import { JSX, useState } from "react";

import { Logo, LogoWhite } from "../assets";

import { GlobalSearch } from "./globalSearch";
import { HelpInformation } from "./helpInformation";
import { UserMenu } from "./userMenu";

export type PortalTab = {
  label: string;
  path: string;
  roles: readonly UserType[];
  overflow?: boolean;
};

const ALL_ROLES: readonly UserType[] = [
  "admin",
  "analyst",
  "broker",
  "agent",
  "consultant",
  "broker_analyst",
];

const ADMIN_AND_CONSULTANT: readonly UserType[] = ["admin", "consultant"];

const tabPath = (route: string): string => `/${route}`;

const firstPathSegment = (path: string): string => path.split("/")[1] ?? "";

const brokersTab: PortalTab = {
  label: "Brokers",
  path: tabPath(ROUTE_NAMES.BROKERS),
  roles: ADMIN_AND_CONSULTANT,
};
const clientsTab: PortalTab = {
  label: "Clients",
  path: tabPath(ROUTE_NAMES.CLIENTS),
  roles: ALL_ROLES,
};
const rfpsTab: PortalTab = {
  label: "RFPs",
  path: tabPath(ROUTE_NAMES.RFPS),
  roles: ALL_ROLES,
};
const consultingTab: PortalTab = {
  label: "Consulting",
  path: tabPath(ROUTE_NAMES.CONSULTING),
  roles: ADMIN_AND_CONSULTANT,
};
const bbeTab: PortalTab = {
  label: "BBE",
  path: tabPath(ROUTE_NAMES.BBE),
  roles: ADMIN_AND_CONSULTANT,
};
const analystsTab: PortalTab = {
  label: "Analysts",
  path: tabPath(ROUTE_NAMES.ANALYSTS),
  roles: ["admin"],
  overflow: true,
};
const mpConfigsTab: PortalTab = {
  label: "MP Configs",
  path: tabPath(ROUTE_NAMES.MP_CONFIGS),
  roles: ["admin"],
  overflow: true,
};
const carrRepsDirTab: PortalTab = {
  label: "Carriers Rep Directory",
  path: tabPath(ROUTE_NAMES.CARRIERS_REP_DIRECTORY),
  roles: ["admin"],
  overflow: true,
};

/** The tabs enabled by feature flags, before any role filtering. */
export const usePortalTabs = (): readonly PortalTab[] => {
  const bbeEnabled = useFeatureIsOn("bbe-feature");
  const consultantViewEnabled = useFeatureIsOn(CONSULTANT_VIEW);
  const carriersRepDirectoryEnabled = useFeatureIsOn(CARRIERS_REP_DIRECTORY);

  return [
    brokersTab,
    clientsTab,
    rfpsTab,
    ...(consultantViewEnabled ? [consultingTab] : []),
    ...(bbeEnabled ? [bbeTab] : []),
    analystsTab,
    mpConfigsTab,
    ...(carriersRepDirectoryEnabled ? [carrRepsDirTab] : []),
  ];
};

/** The tab whose first path segment matches the current path, if any. */
export const findCurrentTab = (
  tabs: readonly PortalTab[],
  currentPath: string,
): PortalTab | undefined =>
  tabs.find(
    (tab) => firstPathSegment(tab.path) === firstPathSegment(currentPath),
  );

export type PortalHeaderUser = {
  givenName?: string;
  familyName?: string;
  userType?: UserType;
  isAdmin?: boolean;
  isAgent?: boolean;
};

type PortalHeaderProps = {
  /** "dashboard" renders the bar transparent with the white logo until the page scrolls. */
  appearance?: "default" | "dashboard";
  /** The host's current location pathname, used to mark the active tab. */
  currentPath: string;
  user: PortalHeaderUser;
  onLogout: () => void;
  /** Present only while the user is impersonating a broker. */
  onExitBrokerView?: () => void;
  helpEmail: string;
  teamEmail: string;
};

export const PortalHeader = ({
  appearance = "default",
  currentPath,
  user: { givenName, familyName, userType, isAdmin, isAgent },
  onLogout,
  onExitBrokerView,
  helpEmail,
  teamEmail,
}: PortalHeaderProps): JSX.Element => {
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const isDashboard = appearance === "dashboard";
  const { isScrolling } = useIsScrolling({ enabled: isDashboard });

  const enabledTabs = usePortalTabs();
  const hideTabs =
    firstPathSegment(currentPath) === ROUTE_NAMES.DOCUMENT_CHECKLIST;
  const tabs = hideTabs
    ? []
    : enabledTabs.filter(
        (tab) => userType !== undefined && tab.roles.includes(userType),
      );
  const currentTab = findCurrentTab(enabledTabs, currentPath)?.path;

  const canRequestHelp = userType !== undefined && !isAdmin;

  const renderPrimaryBar = (): JSX.Element => (
    <PrimaryBar
      tabs={tabs}
      logo={isDashboard && !isScrolling ? LogoWhite : Logo}
      currentTab={currentTab ?? false}
      extras={
        <Box display="flex" alignItems="center" gap={3}>
          <GlobalSearch isAgent={Boolean(isAgent)} />
          {canRequestHelp && (
            <Button
              sx={{
                color: !isDashboard || isScrolling ? "secondary" : "white",
              }}
              variant="text"
              onClick={() => {
                submitEvent("Need Help Clicked");
                setIsHelpOpen(true);
              }}
            >
              Need Help?
            </Button>
          )}
          {givenName && familyName && userType && (
            <UserMenu
              className="user-menu"
              username={`${givenName} ${familyName}`}
              userType={userType}
              logout={onLogout}
              onExitBrokerView={onExitBrokerView}
            />
          )}
        </Box>
      }
    />
  );

  return (
    <>
      <HelpInformation
        isOpen={isHelpOpen}
        setIsOpen={setIsHelpOpen}
        helpEmail={helpEmail}
        teamEmail={teamEmail}
      />
      <AppBar>
        <Container>
          {isScrolling ? (
            <Fade in timeout={{ enter: 1000 }}>
              {renderPrimaryBar()}
            </Fade>
          ) : (
            renderPrimaryBar()
          )}
        </Container>
      </AppBar>
    </>
  );
};
